<template>
  <div class="legend-track">
    <div class="geometry-legend">
      <div class="point-legend grid1">
        <b-form-checkbox
          v-bind:checked="geometryVisible.start"
          class="geometry-check"
          v-on:change="(e) => $_onChangeGeometry('start', e)"
        />
        <div
          class="point-legend-item start"
          v-t="'appAnalyzeLegendTrack.startPoint'"
        />
      </div>
      <div class="point-legend grid2">
        <b-form-checkbox
          v-bind:checked="geometryVisible.end"
          class="geometry-check"
          v-on:change="(e) => $_onChangeGeometry('end', e)"
        />
        <div
          class="point-legend-item end"
          v-t="'appAnalyzeLegendTrack.endPoint'"
        />
      </div>
      <div class="point-legend grid3">
        <b-form-checkbox
          v-bind:checked="geometryVisible.point"
          class="geometry-check"
          v-on:change="(e) => $_onChangeGeometry('point', e)"
        />
        <div
          class="point-legend-item midway"
          v-t="'appAnalyzeLegendTrack.point'"
        />
        <b-form-radio-group
          class="geometry-radio-group"
          v-model="pointSize"
          v-bind:disabled="!geometryVisible.point"
          v-bind:options="pointSizeOptions"
          v-on:change="(e) => $_onChangeGeometry('pointSize', e)"
        />
      </div>
      <div class="line-legend grid4">
        <b-form-checkbox
          v-bind:checked="geometryVisible.line"
          class="geometry-check"
          v-on:change="(e) => $_onChangeGeometry('line', e)"
        />
        <div class="line-legend-item" v-t="'appAnalyzeLegendTrack.line'" />
        <b-form-radio-group
          class="geometry-radio-group"
          v-model="lineThickness"
          v-bind:disabled="!geometryVisible.line"
          v-bind:options="lineThicknessOptions"
          v-on:change="(e) => $_onChangeGeometry('lineThickness', e)"
        />
      </div>
    </div>
    <app-analyze-legend-user
      v-bind:users="usersModified"
      v-bind:visible="visible.user"
      v-on:user-visible-changed="$_onUserVisibleChanged"
      v-on:user-hover-changed="$_onHoverUser"
    />
  </div>
</template>

<script>
import {
  TRACK_POINT_RADIUS,
  TRACK_POINT_LINE_WIDTH,
  TRACK_LINE_WIDTH,
} from '@/constants'
import { mapState } from 'vuex'
import AppAnalyzeLegendUser from './AppAnalyzeLegendUser.vue'

export default {
  name: 'AppAnalyzeLegendTrack',
  components: {
    AppAnalyzeLegendUser,
  },
  props: {
    /**
     * ユーザー
     */
    users: {
      type: Array,
      default: () => [],
    },
    /**
     * 表示状態
     */
    visible: {
      type: Object,
      default: () => ({ geometry: true, user: true }),
    },
  },
  data() {
    return {
      // 測位地点アイコンサイズの選択値
      pointSize: null,
      // 測位軌跡ラインサイズの選択値
      lineThickness: null,
    }
  },
  computed: {
    ...mapState('analyzeTrack', [
      'showPoint',
      'showLine',
      'showStart',
      'showEnd',
      'pointRadius',
      'pointLineWidth',
      'lineWidth',
    ]),
    /**
     * 加工したユーザー
     * @type {Array.<Object>}
     */
    usersModified: function () {
      return this.users.map((u) => ({
        ...u,
      }))
    },
    /**
     * 図形種類の表示状態
     * @type {Object}
     */
    geometryVisible: function () {
      return {
        point: this.showPoint,
        line: this.showLine,
        start: this.showStart,
        end: this.showEnd,
      }
    },
    /**
     * 測位地点サイズ選択肢
     * @type {Array.<Object>}
     */
    pointSizeOptions: function () {
      return [
        {
          text: this.$t('appAnalyzeLegendTrack.pointSize.large'),
          value: {
            radius: TRACK_POINT_RADIUS.LARGE,
            thickness: TRACK_POINT_LINE_WIDTH.LARGE,
          },
        },
        {
          text: this.$t('appAnalyzeLegendTrack.pointSize.medium'),
          value: {
            radius: TRACK_POINT_RADIUS.MEDIUM,
            thickness: TRACK_POINT_LINE_WIDTH.MEDIUM,
          },
        },
        {
          text: this.$t('appAnalyzeLegendTrack.pointSize.small'),
          value: {
            radius: TRACK_POINT_RADIUS.SMALL,
            thickness: TRACK_POINT_LINE_WIDTH.SMALL,
          },
        },
      ]
    },
    /**
     * 測位軌跡サイズ選択肢
     * @type {Array.<Object>}
     */
    lineThicknessOptions: function () {
      return [
        {
          text: this.$t('appAnalyzeLegendTrack.lineThickness.thick'),
          value: TRACK_LINE_WIDTH.LARGE,
        },
        {
          text: this.$t('appAnalyzeLegendTrack.lineThickness.thin'),
          value: TRACK_LINE_WIDTH.SMALL,
        },
      ]
    },
  },
  mounted: function () {
    this.$log.debug('appAnalyzeLegendTrack mounted.')
    // ラジオボタン初期 value 設定
    this.pointSize = {
      radius: this.pointRadius,
      thickness: this.pointLineWidth,
    }
    this.lineThickness = this.lineWidth
  },
  methods: {
    /**
     * ジオメトリ変更時処理
     * @param {Number} type 図形種類
     */
    $_onChangeGeometry: function (type, value) {
      switch (type) {
        case 'start':
          this.$store.commit('analyzeTrack/setShowStart', value)
          break
        case 'end':
          this.$store.commit('analyzeTrack/setShowEnd', value)
          break
        case 'point':
          this.$store.commit('analyzeTrack/setShowPoint', value)
          break
        case 'pointSize':
          this.$store.commit('analyzeTrack/setPointRadius', value.radius)
          this.$store.commit('analyzeTrack/setPointLineWidth', value.thickness)
          break
        case 'line':
          this.$store.commit('analyzeTrack/setShowLine', value)
          break
        case 'lineThickness':
          this.$store.commit('analyzeTrack/setLineWidth', value)
          break
        default:
          break
      }

      const visible = { ...this.geometryVisible }
      visible[type] = !visible[type]
      /**
       * ジオメトリ表示状態変更イベント
       * @param {Object} visible 各図形種類の表示状態
       */
      this.$emit('geometry-visible-changed', visible)
    },
    /**
     * ユーザークリック時処理
     * @param {Number} id ユーザーID
     */
    $_onUserVisibleChanged: function (visible) {
      /**
       * ユーザー表示状態変更イベント
       * @param {Object} visible 各ユーザーの表示状態
       */
      this.$emit('user-visible-changed', visible)
    },
    /**
     * ユーザーホバー時処理
     * @param {Number} id ユーザーID
     */
    $_onHoverUser: function (id) {
      /**
       * ユーザーホバー状態変更イベント
       * @param {Number} id ユーザーID
       */
      this.$emit('user-hover-changed', id)
    },
  },
}
</script>

<style lang="scss" scoped>
$-indicator-size: 14px;

.legend-track {
  width: 100%;
  color: $text-color;
  font-size: $font-size-middle;
  display: flex;
  flex-direction: column;
  row-gap: 24px;
  justify-content: center;
  align-items: center;
}

.geometry-legend {
  width: fit-content;
  height: 92px;
  display: grid;
  justify-content: center;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(3, 1fr);
  grid-row-gap: 8px;
  grid-column-gap: 34px;
}

.grid1 {
  grid-column: 1 / 2;
}
.grid2 {
  grid-column: 2 / 2;
}
.grid3 {
  grid-column: 1 / span 2;
  ::v-deep .custom-control-inline {
    margin-right: 8px;
  }
}
.grid4 {
  grid-column: 1 / span 2;
  ::v-deep .custom-control-inline {
    margin-right: 8px;
  }
}

.geometry-check {
  align-self: center;

  ::v-deep .custom-control-label {
    cursor: pointer;
  }
}

.point-legend {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  column-gap: 8px;

  .point-legend-item {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    column-gap: 8px;
    text-align: left;
    user-select: none;

    &::before {
      flex-shrink: 0;
      flex-grow: 0;
      content: '';
      width: $-indicator-size;
      height: $-indicator-size;
    }
  }

  .start {
    &::before {
      background-image: url(@/assets/ifield/ic_start-point.svg);
      background-size: contain;
      vertical-align: middle;
    }
  }

  .midway {
    &::before {
      border: 3px solid $entity-color-black;
      border-radius: 50%;
    }
  }

  .end {
    &::before {
      background-image: url(@/assets/ifield/ic_goal-point.svg);
      background-size: contain;
      vertical-align: middle;
    }
  }
}

.geometry-radio-group {
  ::v-deep .custom-control-input {
    &:checked ~ .custom-control-label {
      &::before {
        border-color: #ced4da;
        background-color: #ffffff;
      }
      &::after {
        background-image: none; // bootstrap-vueのスタイル打消し
        background-color: #2a8e5c;
        border-radius: 50%;
        width: 8px;
        height: 8px;
        top: calc(50% - 4px);
        left: -1.25rem;
      }
    }
    ~ .custom-control-label {
      cursor: pointer;
      &::after {
        cursor: pointer;
      }
      span {
        user-select: none;
      }
    }
  }
}

.line-legend {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  column-gap: 8px;

  .line-legend-item {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    column-gap: 8px;
    text-align: left;
    user-select: none;

    &::before {
      content: '';
      flex-shrink: 0;
      flex-grow: 0;
      width: $-indicator-size;
      height: 2px;
      background-color: $entity-color-black;
    }
  }
}
</style>
